import { parseTree as parseJsoncTree } from 'jsonc-parser';

import { depthOf } from './depth';
import type { ParseErrorKind, RawParseError } from './tokens';
import { fromJsoncNode, type JsonNode, type JsoncNodeLike } from './tree';

/**
 * Tolerant parse: source in, CST and raw errors out, never an exception.
 *
 * Tolerant is a decision, not laziness (ADR-0007). Run strictly, `jsonc-parser` reports
 * three errors for the single typo in `{a:1}`, twelve for the six in
 * `{'a': True, 'b': None, 'c': False}`, blames the closing brace for a trailing comma at
 * the previous offset, and calls a leading zero a missing comma. Run tolerantly, recovery
 * is cleaner and the CST comes back complete even for a broken document — and the
 * constructs we actually want to report (trailing commas, comments) become invisible here,
 * which is right, because we then find them ourselves in the token stream and anchor the
 * diagnostic on the comma rather than on the brace after it.
 *
 * `rawErrors` is therefore an *input to* `lint/checks/structure.ts`, never a user surface.
 *
 * This file is one of the three allowed to touch `jsonc-parser` (ADR-0007).
 */

/**
 * `jsonc-parser`'s API restated with plain `number`s and no `any`.
 *
 * `ParseError.error` is typed as the ambient `const enum` `ParseErrorCode`, which cannot be
 * named under `verbatimModuleSyntax` (ADR-0022), and `parseTree` returns `Node`, which is
 * cyclic through `parent`. One cast at the seam buys clean types everywhere after it.
 */
interface NumericParseError {
  readonly error: number;
  readonly offset: number;
  readonly length: number;
}

interface TolerantParseOptions {
  readonly disallowComments: boolean;
  readonly allowTrailingComma: boolean;
  readonly allowEmptyContent: boolean;
}

const parseJsonc = parseJsoncTree as unknown as (
  text: string,
  errors: NumericParseError[],
  options: TolerantParseOptions,
) => JsoncNodeLike | undefined;

/** ADR-0007: comments and trailing commas are ours to report, not jsonc's to reject. */
const TOLERANT: TolerantParseOptions = {
  disallowComments: false,
  allowTrailingComma: true,
  allowEmptyContent: true,
};

/**
 * The recursion ceiling this module enforces.
 *
 * `parseTree` was measured to throw `RangeError` at nesting depth 3928 on Node 22, and a
 * Worker stack can be smaller (ADR-0007, ADR-0010). 1000 is a hard structural safety net
 * well under that; the *product* limit is `LintOptions.maxDepth` (500), applied one step
 * earlier in the pipeline so the user gets a `semantic/deep-nesting` diagnostic rather than
 * a silent empty tree.
 */
export const DEFAULT_PARSE_MAX_DEPTH = 1000;

export interface ParseOptions {
  /** Refuse to parse past this nesting depth. Default {@link DEFAULT_PARSE_MAX_DEPTH}. */
  readonly maxDepth?: number;

  /**
   * Depth the caller already measured with `depthOf`, so a 2 MB document is not scanned
   * twice. Omit it and this function measures the depth itself.
   */
  readonly depth?: number;
}

export interface ParseResult {
  /**
   * The tree, or `undefined` — which means one of three things, distinguished by the other
   * two fields: the document was empty or whitespace-only (no errors, `depthExceeded`
   * false), it was too deep (`depthExceeded` true), or jsonc recovered nothing at all from
   * it (errors present).
   */
  readonly tree: JsonNode | undefined;

  /** jsonc's error stream, as data. Normalised into our codes by the structure check. */
  readonly rawErrors: readonly RawParseError[];

  /** Measured nesting depth of the source. */
  readonly depth: number;

  /** `true` when the depth guard refused to run the parser: `tree` is `undefined`. */
  readonly depthExceeded: boolean;
}

const NO_ERRORS: readonly RawParseError[] = Object.freeze([]);

/**
 * Parse `source` tolerantly.
 *
 * Guarded: the depth is measured over the (iterative) token stream first, and the parser is
 * simply not called on a document deeper than `maxDepth`. That guard is why the function
 * can promise never to throw on nesting alone. The `try` around the call is a second net
 * for anything the guard cannot foresee — a pathological input that costs stack for a
 * reason other than depth — and it degrades to "no tree" instead of to a broken tab.
 */
export function parseTree(source: string, options: ParseOptions = {}): ParseResult {
  const maxDepth = options.maxDepth ?? DEFAULT_PARSE_MAX_DEPTH;
  const depth = options.depth ?? depthOf(source);

  if (depth > maxDepth) {
    return { tree: undefined, rawErrors: NO_ERRORS, depth, depthExceeded: true };
  }

  const errors: NumericParseError[] = [];
  let root: JsoncNodeLike | undefined;

  try {
    root = parseJsonc(source, errors, TOLERANT);
  } catch {
    return { tree: undefined, rawErrors: NO_ERRORS, depth, depthExceeded: false };
  }

  return {
    tree: root === undefined ? undefined : fromJsoncNode(root, source.length),
    rawErrors: errors.map((error) => toRawParseError(error, source.length)),
    depth,
    depthExceeded: false,
  };
}

/**
 * Clamped into the document, because jsonc 3.3.1 can report past its end: a source ending
 * in an empty unterminated block comment (`{/*`) yields `UnexpectedEndOfComment` at offset
 * 1 length 3 over three code units, and a `CloseBraceExpected` at offset 3 length 1. A
 * diagnostic span outside the document underlines nothing and breaks `LineIndex`
 * arithmetic, so the overrun is absorbed here — the same bug `lexer.ts` and `tree.ts`
 * absorb for tokens and nodes.
 */
function toRawParseError(error: NumericParseError, sourceLength: number): RawParseError {
  const offset = Math.min(Math.max(error.offset, 0), sourceLength);
  const end = Math.min(Math.max(error.offset + error.length, offset), sourceLength);

  return { kind: error.error as ParseErrorKind, offset, length: end - offset };
}
